// dashboard/frontend/src/components/Campaign/channels/instagram/InstagramDraftDialog.js

import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Typography, CircularProgress,
    Alert, Chip,
} from '@mui/material';
import {
    ContentCopy as CopyIcon, OpenInNew as OpenIcon,
    NavigateNext as NextIcon, NavigateBefore as PrevIcon, Save as SaveIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../../../api/axios';

const InstagramDraftDialog = ({ 
    open, onClose, initialDraft, campaignId, roundId,
    onDraftSent, onSnackbar 
}) => {
    const [draft, setDraft] = useState(initialDraft);
    const [contact, setContact] = useState(null);
    const [editedMessage, setEditedMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [fetchingContact, setFetchingContact] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Fetch full contact details when draft changes
    useEffect(() => {
        const fetchContactDetails = async () => {
            if (!initialDraft) return;
            
            setDraft(initialDraft);
            setEditedMessage(initialDraft.message || '');
            setHasUnsavedChanges(false);
            
            // Get the contact ID from the draft
            const contactId = initialDraft.contactData?.id || initialDraft.contact;
            
            // If contact is already a full object, use it
            if (initialDraft.contactData && typeof initialDraft.contactData === 'object') {
                setContact(initialDraft.contactData);
                return;
            }
            
            // Otherwise, fetch the full contact details
            if (contactId) {
                setFetchingContact(true);
                try {
                    const response = await api.get(`/user-contacts/${contactId}/`);
                    setContact(response.data);
                    console.log('Fetched contact details:', response.data);
                } catch (error) {
                    console.error('Error fetching contact details:', error);
                    onSnackbar({
                        open: true,
                        message: 'Failed to load contact details',
                        severity: 'error'
                    });
                } finally {
                    setFetchingContact(false);
                }
            }
        };

        fetchContactDetails();
    }, [initialDraft, onSnackbar]);

    const handleMessageChange = (e) => {
        setEditedMessage(e.target.value);
        setHasUnsavedChanges(true);
    };

    const handleOpenProfile = () => {
        if (contact?.instagram_url) {
            window.open(contact.instagram_url, '_blank');
        } else {
            onSnackbar({
                open: true,
                message: 'No Instagram URL available',
                severity: 'error'
            });
        }
    };

    const handleCopyAndOpen = async () => {
        try {
            // Copy to clipboard
            await navigator.clipboard.writeText(editedMessage);
            
            // Get the Instagram URL from the contact object
            const igUrl = contact?.instagram_url;
            
            if (!igUrl || igUrl.trim() === '') {
                console.error('Contact data:', contact);
                onSnackbar({
                    open: true,
                    message: 'No Instagram URL found for this contact',
                    severity: 'error'
                });
                return;
            }
            
            // Detect if user is on mobile
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
            
            if (isMobile) {
                // On mobile: Show confirmation, then navigate directly
                onSnackbar({
                    open: true,
                    message: 'Message copied! Opening Instagram...',
                    severity: 'success'
                });
                
                // Small delay to ensure snackbar shows
                setTimeout(() => {
                    // Use direct navigation on mobile instead of window.open
                    // This prevents the blank screen issue
                    window.location.href = igUrl;
                }, 500);
            } else {
                // On desktop: Open in new tab
                const newWindow = window.open(igUrl, '_blank');
                
                if (!newWindow) {
                    onSnackbar({
                        open: true,
                        message: 'Popup blocked! Please allow popups.',
                        severity: 'warning'
                    });
                } else {
                    onSnackbar({
                        open: true,
                        message: 'Message copied! Instagram opened.',
                        severity: 'success'
                    });
                }
            }
        } catch (error) {
            console.error('Error:', error);
            onSnackbar({
                open: true,
                message: 'Error: ' + error.message,
                severity: 'error'
            });
        }
    };

    const handleSave = async () => {
        if (!hasUnsavedChanges) return;
        
        setSaving(true);
        try {
            const response = await api.patch(
                `/contact-message-drafts/${draft.id}/update_ig_dm_message/`,
                { message: editedMessage }
            );
            
            setDraft(response.data.draft);
            setHasUnsavedChanges(false);
            
            onSnackbar({
                open: true,
                message: 'Message saved',
                severity: 'success'
            });
        } catch (error) {
            onSnackbar({
                open: true,
                message: 'Failed to save',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleMarkSentAndNext = async () => {
        setLoading(true);
        try {
            // Save if there are unsaved changes
            if (hasUnsavedChanges) {
                await api.patch(
                    `/contact-message-drafts/${draft.id}/update_ig_dm_message/`,
                    { message: editedMessage }
                );
            }

            // Mark current draft as sent
            await api.post('/contact-message-drafts/mark_ig_dm_sent/', {
                draft_id: draft.id
            });

            onSnackbar({ open: true, message: 'Marked as sent!', severity: 'success' });
            
            // Notify parent to update the list
            if (onDraftSent) {
                onDraftSent(draft.id);
            }

            // Get next draft
            const response = await api.get('/contact-message-drafts/get_next_ig_dm/', {
                params: {
                    campaign_id: campaignId,
                    round_id: roundId,
                    current_draft_id: draft.id
                }
            });

            console.log('Next IG DM response:', response.data);

            if (response.data.has_next && response.data.draft) {
                // Load the next draft
                console.log('Loading next draft:', response.data.draft);
                const nextDraft = response.data.draft;
                setDraft(nextDraft);
                setEditedMessage(nextDraft.message || '');
                setHasUnsavedChanges(false);
                
                // Fetch the contact details for the next draft
                const nextContactId = nextDraft.contactData?.id || nextDraft.contact;
                if (nextContactId) {
                    setFetchingContact(true);
                    try {
                        const contactResponse = await api.get(`/user-contacts/${nextContactId}/`);
                        setContact(contactResponse.data);
                        console.log('Fetched next contact details:', contactResponse.data);
                    } catch (error) {
                        console.error('Error fetching next contact details:', error);
                    } finally {
                        setFetchingContact(false);
                    }
                }
            } else {
                // No more drafts available
                console.log('No more drafts, closing dialog');
                onSnackbar({ 
                    open: true, 
                    message: 'All Instagram DMs sent! Great work! 🎉', 
                    severity: 'success' 
                });
                // Close after a brief delay so user sees the success message
                setTimeout(() => {
                    onClose();
                }, 1500);
            }
        } catch (error) {
            console.error('Error in Mark Sent & Next:', error);
            onSnackbar({ 
                open: true, 
                message: error.response?.data?.error || 'Error processing request', 
                severity: 'error' 
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteLink = async () => {
            if (!contact) return;
            
            const confirmed = window.confirm(
                'Delete Instagram link and draft?\n\n' +
                'This will:\n' +
                '• Remove the Instagram URL from the contact profile\n' +
                '• Delete this draft\n' +
                '• Delete any other Instagram drafts for this contact\n\n' +
                'This action cannot be undone.'
            );
            
            if (!confirmed) return;
            
            setDeleting(true);
            try {
                // Get next draft FIRST, before deleting
                const nextResponse = await api.get('/contact-message-drafts/get_next_ig_dm/', {
                    params: {
                        campaign_id: campaignId,
                        round_id: roundId,
                        current_draft_id: draft.id
                    }
                });
                
                // Store the next draft info
                const nextDraftInfo = nextResponse.data.has_next ? nextResponse.data.draft : null;
                
                // NOW delete the IG link
                await api.post(`/user-contacts/${contact.id}/delete_instagram_link/`);
                
                onSnackbar({
                    open: true,
                    message: 'Instagram link and draft(s) deleted',
                    severity: 'success'
                });
                
                if (onDraftSent) {
                    onDraftSent(draft.id);
                }
                
                // Load the next draft we fetched earlier
                if (nextDraftInfo) {
                    setDraft(nextDraftInfo);
                    setEditedMessage(nextDraftInfo.message || '');
                    setHasUnsavedChanges(false);
                    
                    const nextContactId = nextDraftInfo.contactData?.id || nextDraftInfo.contact;
                    if (nextContactId) {
                        setFetchingContact(true);
                        try {
                            const contactResponse = await api.get(`/user-contacts/${nextContactId}/`);
                            setContact(contactResponse.data);
                        } catch (error) {
                            console.error('Error fetching next contact:', error);
                        } finally {
                            setFetchingContact(false);
                        }
                    }
                } else {
                    onSnackbar({ 
                        open: true, 
                        message: 'No more drafts available', 
                        severity: 'info' 
                    });
                    setTimeout(() => onClose(), 1500);
                }
            } catch (error) {
                console.error('Failed to delete Instagram link:', error);
                onSnackbar({
                    open: true,
                    message: error.response?.data?.error || 'Failed to delete Instagram link',
                    severity: 'error'
                });
            } finally {
                setDeleting(false);
            }
        };

    const handleNextOnly = async () => {
        setLoading(true);
        try {
            // Get next draft WITHOUT marking current as sent
            const response = await api.get('/contact-message-drafts/get_next_ig_dm/', {
                params: {
                    campaign_id: campaignId,
                    round_id: roundId,
                    current_draft_id: draft.id
                }
            });

            console.log('Next IG DM response:', response.data);

            if (response.data.has_next && response.data.draft) {
                // Load the next draft
                console.log('Loading next draft:', response.data.draft);
                const nextDraft = response.data.draft;
                setDraft(nextDraft);
                setEditedMessage(nextDraft.message || '');
                setHasUnsavedChanges(false);
                
                // Fetch the contact details for the next draft
                const nextContactId = nextDraft.contactData?.id || nextDraft.contact;
                if (nextContactId) {
                    setFetchingContact(true);
                    try {
                        const contactResponse = await api.get(`/user-contacts/${nextContactId}/`);
                        setContact(contactResponse.data);
                        console.log('Fetched next contact details:', contactResponse.data);
                    } catch (error) {
                        console.error('Error fetching next contact details:', error);
                    } finally {
                        setFetchingContact(false);
                    }
                }
                
                onSnackbar({ 
                    open: true, 
                    message: 'Loaded next contact', 
                    severity: 'info' 
                });
            } else {
                // No more drafts available
                console.log('No more drafts available');
                onSnackbar({ 
                    open: true, 
                    message: 'No more drafts available', 
                    severity: 'info' 
                });
            }
        } catch (error) {
            console.error('Error loading next draft:', error);
            onSnackbar({ 
                open: true, 
                message: error.response?.data?.error || 'Error loading next draft', 
                severity: 'error' 
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePreviousOnly = async () => {
            setLoading(true);
            try {
                // Get previous draft
                const response = await api.get('/contact-message-drafts/get_previous_ig_dm/', {
                    params: {
                        campaign_id: campaignId,
                        round_id: roundId,
                        current_draft_id: draft.id
                    }
                });

                if (response.data.has_previous && response.data.draft) {
                    const prevDraft = response.data.draft;
                    setDraft(prevDraft);
                    setEditedMessage(prevDraft.message || '');
                    setHasUnsavedChanges(false);
                    
                    const prevContactId = prevDraft.contactData?.id || prevDraft.contact;
                    if (prevContactId) {
                        setFetchingContact(true);
                        try {
                            const contactResponse = await api.get(`/user-contacts/${prevContactId}/`);
                            setContact(contactResponse.data);
                        } catch (error) {
                            console.error('Error fetching previous contact:', error);
                        } finally {
                            setFetchingContact(false);
                        }
                    }
                    
                    onSnackbar({ 
                        open: true, 
                        message: 'Loaded previous contact', 
                        severity: 'info' 
                    });
                } else {
                    onSnackbar({ 
                        open: true, 
                        message: 'No previous drafts available', 
                        severity: 'info' 
                    });
                }
            } catch (error) {
                onSnackbar({ 
                    open: true, 
                    message: 'No previous drafts available', 
                    severity: 'info' 
                });
            } finally {
                setLoading(false);
            }
        };    

    if (!draft) return null;

    const charCount = editedMessage.length;
    const charLimit = 450;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            fullScreen={isMobile} // Full screen on mobile
            PaperProps={{
                sx: {
                    // Better mobile positioning
                    ...(isMobile && {
                        margin: 0,
                        maxHeight: '100%',
                    })
                }
            }}
        >    
            <DialogTitle 
                sx={{ 
                    pb: { xs: 1, sm: 2 },
                    pt: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                }}>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            fontSize: { xs: '1.1rem', sm: '1.25rem' } // Slightly smaller on mobile
                        }}
                    >
                        Instagram DM v2
                    </Typography>
                    <Chip 
                        label={`${charCount}/${charLimit}`}
                        color={charCount > charLimit ? 'error' : 'default'}
                        size="small"
                        sx={{
                            fontWeight: 'bold',
                        }}
                    />
                </Box>
            </DialogTitle>
            <DialogContent>
                {/* Contact Info Section */}
                {fetchingContact ? (
                    <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Loading contact details...
                        </Typography>
                    </Box>
                ) : contact ? (
                    <Box sx={{ 
                        mb: { xs: 2, sm: 3 }, 
                        p: { xs: 1.5, sm: 2 }, 
                        backgroundColor: 'grey.100', 
                        borderRadius: 1 
                    }}>
                        <Typography 
                            variant="subtitle1" 
                            fontWeight="bold"
                            sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                        >
                            {contact.first_name} {contact.last_name}
                        </Typography>
                        {contact.company_name && (
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                            >
                                {contact.company_name}
                            </Typography>
                        )}
                        {contact.title && (
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                            >
                                {contact.title}
                            </Typography>
                        )}
                        {contact.instagram_url && (
                            <Typography 
                                variant="body2" 
                                color="primary" 
                                sx={{ 
                                    mt: 1, 
                                    cursor: 'pointer',
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    wordBreak: 'break-all', // Prevent overflow on mobile
                                }}
                                onClick={handleOpenProfile}
                            >
                                {contact.instagram_url}
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                        <Alert severity="warning">
                            Contact details not available
                        </Alert>
                    </Box>
                )}

                {/* Message Editor - ALWAYS EDITABLE */}
                <Box sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            Message {hasUnsavedChanges && <span style={{ color: 'orange' }}>*</span>}
                        </Typography>
                        {hasUnsavedChanges && (
                            <Button
                                size="small"
                                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                                variant="outlined"
                            >
                                Save
                            </Button>
                        )}
                    </Box>

                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        value={editedMessage}
                        onChange={handleMessageChange}
                        variant="outlined"
                        placeholder="Edit your Instagram DM here..."
                    />
                </Box>

                {charCount > charLimit && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Message exceeds {charLimit} character limit
                    </Alert>
                )}

            </DialogContent>

            <DialogActions 
                sx={{ 
                    px: { xs: 2, sm: 3 }, 
                    pb: { xs: 2, sm: 2 },
                    pt: { xs: 1, sm: 1 },
                    gap: { xs: 1, sm: 1 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'stretch',
                }}
            >
                {/* Delete IG Link - First on mobile, in button flow on desktop */}
                <Button 
                    onClick={handleDeleteLink} 
                    startIcon={<DeleteIcon />} 
                    variant="outlined"
                    color="error"
                    disabled={deleting || loading || !contact?.instagram_url}
                    size="large"
                    fullWidth
                    sx={{
                        order: { xs: 6, sm: 0 },
                        py: { xs: 1.5, sm: 1 },
                        px: 2,
                        fontSize: { xs: '1rem', sm: '0.875rem' },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        '& .MuiButton-startIcon': {
                            margin: 0,
                            marginRight: { xs: 0.5, sm: 1 },
                        }
                    }}
                >
                    {deleting ? 'Deleting...' : 'Delete'}
                </Button>

                {/* Copy & Open - PRIMARY on mobile, top position */}
                <Button 
                    onClick={handleCopyAndOpen} 
                    startIcon={<OpenIcon />} 
                    variant="contained" 
                    color="primary"
                    disabled={deleting || !contact?.instagram_url}
                    size="large"
                    fullWidth
                    sx={{
                        order: { xs: 1, sm: 4 },
                        py: { xs: 1.5, sm: 1 },
                        fontSize: { xs: '1rem', sm: '0.875rem' },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        '& .MuiButton-startIcon': {
                            margin: 0,
                            marginRight: { xs: 0.5, sm: 1 },
                        }
                    }}
                >
                    Open
                </Button>

                {/* Mark Sent - Second on mobile */}
                <Button
                    onClick={handleMarkSentAndNext}
                    variant="contained"
                    color="success"
                    disabled={deleting || loading}
                    size="large"
                    fullWidth
                    sx={{
                        order: { xs: 2, sm: 5 },
                        py: { xs: 1.5, sm: 1 },
                        px: 2,
                        fontSize: { xs: '1rem', sm: '0.875rem' },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {loading ? 'Processing...' : 'Mark Sent'}
                </Button>

                {/* Previous & Next in one row on mobile */}
                <Box 
                    sx={{ 
                        order: { xs: 3, sm: 6 },
                        display: 'flex',
                        gap: 1,
                        width: '100%',
                    }}
                >
                    {/* Previous Button */}
                    <Button
                        onClick={handlePreviousOnly}
                        variant="outlined"
                        color="primary"
                        disabled={deleting || loading}
                        size="large"
                        sx={{
                            py: { xs: 1.5, sm: 1 },
                            px: 2,
                            fontSize: { xs: '1.5rem', sm: '1.5rem' },
                            minWidth: { xs: 60, sm: 60 },
                            flex: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <PrevIcon />
                    </Button>

                    {/* Next Button */}
                    <Button
                        onClick={handleNextOnly}
                        variant="outlined"
                        color="primary"
                        disabled={deleting || loading}
                        size="large"
                        sx={{
                            py: { xs: 1.5, sm: 1 },
                            px: 2,
                            fontSize: { xs: '1.5rem', sm: '1.5rem' },
                            minWidth: { xs: 60, sm: 60 },
                            flex: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <NextIcon />
                    </Button>
                </Box>

                {/* Spacer for desktop only */}
                <Box 
                    sx={{ 
                        flexGrow: 1,
                        display: { xs: 'none', sm: 'block' },
                        order: 1 
                    }} 
                />

                {/* Close button - Last on mobile */}
                <Button 
                    onClick={onClose}
                    variant="text"
                    size="large"
                    fullWidth
                    sx={{
                        order: { xs: 6, sm: 2 },
                        py: { xs: 1.5, sm: 1 },
                        px: 2,
                        fontSize: { xs: '1rem', sm: '0.875rem' },
                        color: 'text.secondary',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InstagramDraftDialog;